/**
 * drawerLayout — the work-item drawer's SPINE (design pass, soak r2).
 *
 * Founder report 2026-07-25: the drawer is "getting long, beige and with
 * chaotic and not quite aligned formatting". That is the record page's
 * wall-of-beige problem (adjudicated R11-R14, 2026-07-06) arriving in a
 * 440px column, plus five competing left edges in one scroll. This file
 * is the fix, and it deliberately speaks the SAME design language the
 * record page and the Directory detail rebuild already speak — it does
 * not invent a drawer dialect:
 *
 *   SURFACE (the beige fix). The drawer body is the quiet cream ground;
 *   each section is a card that earns its own surface, exactly the DL4
 *   ladder:
 *     - `paper`  — THE card recipe (rounded-card bg-paper shadow-sh1
 *                  border border-sage-800/[8%]): the work. Summary,
 *                  checklist, activity.
 *     - `tint`   — the DL4 TINT ZONE (rounded-card bg-sage-50 border
 *                  border-sage-200): standing setup rather than the work.
 *                  Intake details (facts captured at the call) is the
 *                  drawer's one tint zone, and reads as setup at a glance.
 *   No new colors: sage-50 / paper / cream / sage-800 at 6-8%, all
 *   existing tokens (raw-grey lint stays green).
 *
 *   GRID (the alignment fix). ONE label column for the whole drawer:
 *   LABEL_COL. Summary rows, the intake edit form, section action rows
 *   and the checklist all start on the same two left edges (the label
 *   edge and the value edge). The old drawer had the summary at a 122px
 *   label column, the intake form on a 2-up grid with labels ABOVE the
 *   controls, the reversibility row on no grid at all, and the lifecycle
 *   row nudged with a hand-tuned -ml-2. They now share this file.
 *
 *   ACTION_INSET is the one sanctioned exception, and it is an OPTICAL
 *   alignment, not a second grid: a TextAction pill carries its own
 *   px-2, so a row of pills has to be pulled back by exactly that much
 *   for its TEXT to land on the grid line the labels above it use. One
 *   constant, one comment, every action row.
 *
 * Rows divide with `divide-y divide-sage-800/[6%]` (DL6) — never the
 * stacked dashed borders the drawer used to hand-roll per row.
 *
 * No hand-rolled button chrome lives here: the disclosure affordance
 * composes the Button primitive (the ProjectionCard section-collapse
 * pattern), so this file needs no raw-button allowlist entry.
 */

import { useId, useState, type ReactNode } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '@/components/primitives/Button';

/**
 * THE label column. Every label:value pair in the drawer — read rows and
 * edit rows alike — sits on this grid, so labels share one left edge and
 * values share another. 104px holds "Care manager" and "Contact method"
 * at 13px without wrapping inside the 440px default drawer.
 *
 * A row stacks, the value under its label, when its section body is
 * under 236px wide: the 104px label, the 12px gap and the narrowest value
 * the drawer keeps beside a label (120px). The threshold is in px, like
 * the grid, so a larger root font never stacks a row that fits; it reads
 * the section body, which DrawerSection makes an inline-size container.
 */
export const LABEL_COL = 'grid grid-cols-1 @[236px]:grid-cols-[104px_minmax(0,1fr)] items-start gap-x-3';

/**
 * The optical inset for a row of TextAction pills. A pill's own px-2
 * padding would otherwise push its text 8px past the label edge every
 * label above it lands on. Applied to the pill CONTAINER, never to
 * individual pills, so a wrapping row stays aligned on every line.
 */
export const ACTION_INSET = '-ml-2';

/** The section-title register (DL9 eyebrow: 11.5px caps, 0.16em, sage-400). */
export const SECTION_EYEBROW =
  'text-[11.5px] font-semibold uppercase tracking-[0.16em] text-sage-400';

/** The two sanctioned section surfaces (DL4). */
export type DrawerSectionTone = 'paper' | 'tint';

const TONE_CLASSES: Record<DrawerSectionTone, string> = {
  paper: 'rounded-card bg-paper shadow-sh1 border border-sage-800/[8%]',
  tint: 'rounded-card bg-sage-50 border border-sage-200',
};

/**
 * A per-drawer-section open/closed preference, persisted per browser
 * (the PipelineBoard collapsed-lane precedent: a VIEW preference, never
 * shared team state). `defaultOpen` is the FIRST-VISIT state only; once
 * the operator has toggled, their stored choice wins.
 */
export function useDrawerDisclosure(
  storageKey: string | null,
  defaultOpen: boolean,
): { open: boolean; toggle: () => void } {
  const [open, setOpen] = useState<boolean>(() => {
    if (storageKey === null) return defaultOpen;
    try {
      const stored = window.localStorage.getItem(storageKey);
      if (stored === 'open') return true;
      if (stored === 'closed') return false;
    } catch {
      /* localStorage unavailable (private mode) — the default stands. */
    }
    return defaultOpen;
  });
  const toggle = (): void =>
    setOpen((v) => {
      const next = !v;
      if (storageKey !== null) {
        try {
          window.localStorage.setItem(storageKey, next ? 'open' : 'closed');
        } catch {
          /* localStorage unavailable — the state stays in memory. */
        }
      }
      return next;
    });
  return { open, toggle };
}

export interface DrawerSectionProps {
  /** The section title, rendered in the eyebrow register. */
  title: string;
  /** A quiet one-line note beside the title (e.g. what the section is for). */
  hint?: string;
  /**
   * Quiet metadata beside the title — a count, or a collapsed summary.
   * A collapsed section must never hide THAT it holds something.
   */
  meta?: ReactNode;
  /** Right-aligned header slot (one quiet action). */
  action?: ReactNode;
  tone?: DrawerSectionTone;
  /** Omit for an always-open section. */
  collapsible?: boolean;
  /** First-visit open state for a collapsible section (default open). */
  defaultOpen?: boolean;
  /** Persists the operator's toggle per browser. Omit for in-memory only. */
  storageKey?: string;
  /** Drops the body's own padding — for a section whose child owns its rows. */
  flush?: boolean;
  testId?: string;
  children: ReactNode;
}

/**
 * One drawer section: a titled card on one of the two sanctioned
 * surfaces, optionally a disclosure. The header stays put when the body
 * folds, and `meta` keeps existence visible on a collapsed section. *
 * The body (padded or flush) is an inline-size container (`@container`),
 * the width LABEL_COL's rows stack against. Containment makes the body
 * the containing block for absolutely and fixed positioned descendants
 * and a stacking context, so a dropdown inside a section portals out or
 * stays inside the body.
 */
export function DrawerSection({
  title,
  hint,
  meta,
  action,
  tone = 'paper',
  collapsible = false,
  defaultOpen = true,
  storageKey,
  flush = false,
  testId,
  children,
}: DrawerSectionProps): ReactNode {
  const { open, toggle } = useDrawerDisclosure(storageKey ?? null, defaultOpen);
  const bodyId = useId();
  const shown = !collapsible || open;

  return (
    <section data-testid={testId} data-open={shown ? 'true' : 'false'} className={TONE_CLASSES[tone]}>
      <header className={`flex items-center gap-2 px-4 pt-3 ${shown ? 'pb-2' : 'pb-3'}`}>
        {collapsible ? (
          <Button
            variant="ghost"
            size="sm"
            className="!min-h-0 -ml-1.5 min-w-0 flex-1 !justify-start !px-1.5 !py-0.5"
            data-testid={testId ? `${testId}-toggle` : undefined}
            aria-expanded={open}
            // Only reference the body while it is mounted (a folded body is
            // not rendered, so aria-controls would dangle).
            aria-controls={open ? bodyId : undefined}
            aria-label={open ? `Collapse ${title}` : `Expand ${title}`}
            icon={
              open ? (
                <ChevronDown size={14} aria-hidden="true" />
              ) : (
                <ChevronRight size={14} aria-hidden="true" />
              )
            }
            onClick={toggle}
          >
            <span className={SECTION_EYEBROW}>{title}</span>
            {meta !== undefined && meta !== null && (
              <span className="ml-1 text-[11.5px] font-medium normal-case tracking-normal text-ink-soft">
                {meta}
              </span>
            )}
          </Button>
        ) : (
          <>
            <h3 className={SECTION_EYEBROW}>{title}</h3>
            {meta !== undefined && meta !== null && (
              <span className="text-[11.5px] font-medium text-ink-soft">{meta}</span>
            )}
          </>
        )}
        {action && <span className="ml-auto shrink-0">{action}</span>}
      </header>
      {hint && shown && (
        <p className="px-4 pb-1 text-[11.5px] italic text-ink-soft">{hint}</p>
      )}
      {shown && (
        <div id={bodyId} className={flush ? '@container pb-1' : '@container px-4 pb-3'}>
          {children}
        </div>
      )}
    </section>
  );
}

/**
 * One label:value row on THE grid. Replaces the drawer's old per-row
 * dashed-border Field (the dividers now come from the list, DL6). A value
 * with a word longer than its column (a long name, an email address)
 * wraps inside the column instead of pushing the drawer sideways.
 */
export function DrawerRow({
  label,
  children,
  testId,
}: {
  label: string;
  children: ReactNode;
  testId?: string;
}): ReactNode {
  return (
    <div data-testid={testId} className={`${LABEL_COL} py-1.5 text-[13px]`}>
      <span className="text-sage-400">{label}</span>
      <span className="min-w-0 [overflow-wrap:anywhere] text-ink">{children}</span>
    </div>
  );
}

/**
 * A row of inline actions on THE grid: the label column keeps its word,
 * the pills sit in the value column with the optical inset so their text
 * lands on the value edge every row above them uses.
 */
export function DrawerActionRow({
  label,
  children,
  testId,
}: {
  label: string;
  children: ReactNode;
  testId?: string;
}): ReactNode {
  return (
    <div data-testid={testId} className={`${LABEL_COL} py-1.5 text-[13px]`}>
      <span className="pt-0.5 text-sage-400">{label}</span>
      <span className={`flex min-w-0 flex-wrap items-center gap-x-1 gap-y-1 ${ACTION_INSET}`}>
        {children}
      </span>
    </div>
  );
}

/** The row list wrapper — one divider rule for every stack of rows. */
export function DrawerRowList({ children }: { children: ReactNode }): ReactNode {
  return <div className="divide-y divide-sage-800/[6%]">{children}</div>;
}
